// @ts-check
import { gql, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import React from 'react';
import { Box, Button, Link, StarIcon } from '~/design-system';
import DefaultImportInProgressPage from '../../../../components/ImportComplete/DefaultImportInProgressPage';
import DefaultImportStateErrorPage from '../../../../components/ImportComplete/DefaultImportStateErrorPage';
import DefaultImportWithErrorPage from '../../../../components/ImportComplete/DefaultImportWithErrorPage';
import Layout from '../../../../components/Layout/Layout';
import PageSubtitle from '../../../../components/Layout/PageSubtitle';
import PageTitle from '../../../../components/Layout/PageTitle';
import { useCommonLinks } from '../../_config';
const PORTFOLIO_HOSTNAME = import.meta.env.VITE_PORTFOLIO_HOSTNAME;

export default function Complete({ match }) {
  const { workspaceId, importId } = match.params;
  const { loading, data, startPolling, stopPolling } = useQuery(query, {
    variables: {
      workspaceId,
      importId,
    },
  });
  const importData = data?.workspace?.dataImport;
  const state = importData?.state?.state;
  React.useEffect(() => {
    if (['STARTED', 'JOB_IN_PROGRESS'].includes(state)) {
      startPolling(1000);
    }
    return () => {
      stopPolling();
    };
  }, [data, state, startPolling, stopPolling]);

  if (loading) return null;
  if (state !== 'JOB_IN_PROGRESS') {
    stopPolling();
  }

  if (state === 'JOB_IN_PROGRESS') {
    return <DefaultImportInProgressPage importData={importData} />;
  }
  if (state === 'COMPLETED') {
    return <ImportComplete importData={importData} />;
  }
  if (state === 'ERROR') {
    return <DefaultImportWithErrorPage importData={importData} />;
  }
  return <DefaultImportStateErrorPage importData={importData} />;
}

function ImportComplete({ importData }) {
  const link = useCommonLinks();
  const { collectionId, importId } = importData;
  return (
    <Layout>
      <Box>
        <Box sx={{ textAlign: 'center' }}>
          <StarIcon sx={{ height: 120, width: '120px' }} />
          <PageTitle>
            <Trans>Import complete</Trans>
          </PageTitle>
          <PageSubtitle>
            <Trans>Your data was successfully imported.</Trans>
          </PageSubtitle>
          <Box sx={{ marginTop: 5 }}>
            <Button variant="primary" to={link.root()}>
              <Trans>View imports</Trans>
            </Button>
          </Box>
          <Box sx={{ marginTop: 3 }}>
            <Link
              href={`${PORTFOLIO_HOSTNAME}/dashboard/app/catalogs/${collectionId}/wines`}
              target="_blank"
            >
              <Trans>Go to collection</Trans>
            </Link>
          </Box>
          <Box sx={{ marginTop: 3 }}>
            <Link
              to={link.sendNotification({
                importId,
                importType: 'productMasterListImport',
              })}
            >
              <Trans>Send email</Trans>
            </Link>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
}

const query = gql`
  query ImportCompletePage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        collectionId
        state {
          state
          progress
        }
      }
    }
  }
`;
